import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { AppUtils } from 'app/main/utils/appUtils';
import { AlertComponentComponent } from '../../alert/alert-component/alert-component.component';
import { ApiService } from '../../services/api.service';
import { LoaderService } from '../../services/loaderService.service';
import { LocalAccountService } from '../../services/localAccount.service';


@Component({
    selector: 'app-files-uploader',
    templateUrl: './files-uploader.component.html',
    styleUrls: ['./files-uploader.component.scss']
})
export class FilesUploaderComponent implements OnInit, AfterViewInit {

    public static BUTTON_ID_VALUE: string = AppUtils.buildUuid();

    @Input() forceReset: boolean;
    @Input() showResumeTitle: boolean;
    @Input() multiple: boolean;
    @Input() fileParam: string;
    @Input() endpoint: string;
    @Input() accept: string;
    @Input() matIconName: string;
    @Input() buttonLabel: string;
    @Input() alreadyPresentFiles: any;
    @Input() fileIdsSeparator: string;
    @Input() tipologia: string;
    @Output() tempFilesToSave: EventEmitter<string>;
    @Input() onlyJpeg: boolean;
    @Input() onlyJpegAndPdf: boolean;
    @Input() buttonClass: string;
    @Input() removeMarkedContentOnRender: boolean;
    @Input() suffixToRemoveOnRender: string;
    @Input() prefixToRemoveOnRender: string;
    @Output() fileInput = new EventEmitter<ElementRef>();

    buttonId: string;
    errorMessage: string;
    formData: FormData;
    operationInProgress: boolean;
    initialized: boolean;
    tempFilesMap: Map<string, any>;
    tempFilesArray: Array<any>;
    alreadyPresentFilesMap: Map<string, any>;
    @ViewChild('singleFileInput') singleFileInput: ElementRef;
    @ViewChild('multipleFileInput') multipleFileInput: ElementRef;


    constructor(
        private apiService: ApiService,
        private localAccountService: LocalAccountService,
        private loaderService: LoaderService,
        private matDialog: MatDialog) {
        this.initialize();
    }

    // tslint:disable-next-line:use-life-cycle-interface
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['forceReset']) {
            this.forceReset = changes['forceReset'].currentValue;
            this.manageForceReset();
        }
        if (changes['removeMarkedContentOnRender']) {
            this.removeMarkedContentOnRender = changes['removeMarkedContentOnRender'].currentValue;
        }
        if (changes['suffixToRemoveOnRender']) {
            this.suffixToRemoveOnRender = changes['suffixToRemoveOnRender'].currentValue;
        }
        if (changes['prefixToRemoveOnRender']) {
            this.prefixToRemoveOnRender = changes['prefixToRemoveOnRender'].currentValue;
        }
        if (changes['multiple']) {
            this.multiple = changes['multiple'].currentValue;
        }
        if (changes['showResumeTitle']) {
            this.showResumeTitle = changes['showResumeTitle'].currentValue;
        }
        if (changes['fileParam']) {
            this.fileParam = changes['fileParam'].currentValue;
        }
        if (changes['endpoint']) {
            this.endpoint = changes['endpoint'].currentValue;
        }
        if (changes['accept']) {
            this.accept = changes['accept'].currentValue;
        }
        if (changes['matIconName']) {
            this.matIconName = changes['matIconName'].currentValue;
        }
        if (changes['buttonLabel']) {
            this.buttonLabel = changes['buttonLabel'].currentValue;
        }
        if (changes['buttonClass']) {
            this.buttonClass = changes['buttonClass'].currentValue;
        }
        if (changes['fileIdsSeparator']) {
            this.fileIdsSeparator = changes['fileIdsSeparator'].currentValue;
        }
        if (changes['alreadyPresentFiles']) {
            this.alreadyPresentFiles = changes['alreadyPresentFiles'].currentValue;
            this.manageAlreadyPresentFiles();
        }

    }
    ngAfterViewInit(): void {
        this.fileInput.emit(this.singleFileInput);
    }

    manageAlreadyPresentFiles(): void {
        if (this.alreadyPresentFiles && this.alreadyPresentFiles.length > 0) {
            for (const currFile of this.alreadyPresentFiles) {
                this.alreadyPresentFilesMap.set(currFile.fileId, currFile);
            }
        }
    }

    ngOnInit(): void {
        this.initialized = false;
        this.checkInputParams();
        this.initialized = true;
    }

    initialize(): void {
        this.buttonId = FilesUploaderComponent.BUTTON_ID_VALUE;
        this.initialized = false;
        this.formData = new FormData();
        this.operationInProgress = false;
        this.tempFilesMap = new Map<string, any>();
        this.tempFilesArray = new Array<any>();
        this.alreadyPresentFilesMap = new Map<string, any>();
        this.errorMessage = null;
        this.buttonClass = null;
        this.tempFilesToSave = new EventEmitter<string>();
        this.showResumeTitle = true;
        this.removeMarkedContentOnRender = false;
        this.forceReset = false;
    }

    checkInputParams(): void {
        if (!this.fileParam) {
            this.fileParam = 'file';
        }
        if (!this.endpoint) {
            if (this.multiple) {
                this.endpoint = 'files/multiple/temp/upload';
            } else {
                this.endpoint = 'files/single/temp/upload';
            }
        }
        if (!this.accept) {
            this.accept = '*/*';
        }
        if (!this.matIconName) {
            this.matIconName = 'cloud_upload';
        }
        if (!this.fileIdsSeparator) {
            this.fileIdsSeparator = ',';
        }

        if (!this.buttonClass) {
            this.buttonClass = 'upload-files-button primary';
        } else {
            this.buttonClass = 'upload-files-button ' + this.buttonClass;
        }
    }

    onFileInputChange(event): void {
        this.reset();
        const filesToUpload = event.target.files as Array<File>;
        if (filesToUpload && filesToUpload.length > 0) {
            this.loaderService.showLoader();
            this.operationInProgress = true;
            for (const file of filesToUpload) {
                this.formData.append(this.fileParam, file, file.name);
            }
            const authToken = this.localAccountService.getToken();
            this.apiService.postFileRequestPromise(this.endpoint, this.formData).then((res) => {
                this.manageUploadedResponse(res);
                this.operationInProgress = false;
            }).catch((err) => {
                this.manageUploadedResponse(err);
                this.operationInProgress = false;
            }).finally(() => {
                this.loaderService.hideLoader();
            });
        }
    }

    reset(): void {
        this.formData = new FormData();
        this.errorMessage = null;
    }

    manageUploadedResponse(res): void {
        if (res && res.ok && res.payload && res.payload.files && res.payload.files.length > 0) {
            for (const currFile of res.payload.files) {
                this.tempFilesMap.set(currFile.fileId, currFile);
            }
            this.emitFiles();
        } else {
            // TRY TO RETRIEVE ERROR MESSAGE FROM SERVER, IF ANY, WHEN API UTILS IS USED!!! (DOMENICOOOOOO)
            this.errorMessage = 'C\'e\' stato un errore imprevisto, riprovare';
            // this.errorMessage = res.error.message
        }
    }

    emitFiles(): void {
        this.tempFilesArray = Array.from(this.tempFilesMap.values());
        let filesId = '';
        if (this.tempFilesArray && this.tempFilesArray.length > 0) {
            this.tempFilesArray.forEach((file, i) => {
                if (i === 0) {
                    filesId = file.fileId;
                } else {
                    filesId = filesId + this.fileIdsSeparator + file.fileId;
                }
            });
        }
        this.tempFilesToSave.emit(filesId);
    }

    onDeleteFile(file): void {
        if (file) {
            if (file.deleteFileUrl) {
                this.loaderService.showLoader();
                this.operationInProgress = true;
                const authToken = this.localAccountService.getToken();
                this.apiService.deleteRequestPromise(file.deleteFileUrl).then((res) => {
                    this.localDelete(false, file.fileId);
                    this.operationInProgress = false;
                }).catch((err) => {
                    this.operationInProgress = false;
                }).finally(() => {
                    this.loaderService.hideLoader();
                });
            } else {
                this.localDelete(true, file.fileId);
            }
        }
    }

    localDelete(tempFile, fileId): void {
        if (fileId) {
            if (tempFile) {
                this.tempFilesMap.delete(fileId);
                this.emitFiles();
            } else {
                this.alreadyPresentFilesMap.delete(fileId);
                this.updateAlreadyPresentFiles();
            }
        }
    }

    updateAlreadyPresentFiles(): void {
        this.alreadyPresentFiles = Array.from(this.alreadyPresentFilesMap.values());
    }

    onDownloadFile(file): void {
        if (file) {
            if (file.downloadFileUrl) {
                this.loaderService.showLoader();
                this.operationInProgress = true;
                this.apiService.downloadFile(file.downloadFileUrl, file.fileId).then((res) => {
                    this.operationInProgress = false;
                }).catch((err) => {
                    this.operationInProgress = false;
                }).finally(() => {
                    this.loaderService.hideLoader();
                });
            }
        }
    }

    onViewFile(file): void {
        if (file) {
            if (file.renderFileUrl) {
                this.operationInProgress = true;
                this.apiService.previewFile(file.renderFileUrl, file.fileId).then((res) => {
                    this.operationInProgress = false;
                }).catch((err) => {
                    this.operationInProgress = false;
                });
            }
        }
    }

    async deleteConfirmation(file) {
        const dialog = this.matDialog.open(AlertComponentComponent, {
            data: {
                innerHTML: true,
                title: 'Sei sicuro di voler procedere?',
                buttons: [
                    {
                        text: 'Elimina',
                        color: 'warn',
                        close: true,
                        value: true,
                        callback: async () => {
                            this.onDeleteFile(file);
                        }
                    },
                    {
                        text: 'Annulla',
                        close: true,
                        value: false,
                        callback: () => {
                        }
                    }
                ]
            }
        });
        return await dialog.afterClosed().toPromise();
    }

    onSingleFileInputButtonClick(event) {
        if (event.isTrusted) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.singleFileInput.nativeElement.click();
    }

    onMultipleFileInputButtonClick(event) {
        if (event.isTrusted) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.multipleFileInput.nativeElement.click();
    }

    removeTMPSuffixIfAny(fileName) {
        let ret = fileName;
        if (this.removeMarkedContentOnRender) {
            ret = AppUtils.removeMarkedContentPreservingEndMarker(fileName, this.prefixToRemoveOnRender, this.suffixToRemoveOnRender);
        }
        return ret;
    }

    manageForceReset() {
        if (this.forceReset) {
            this.initialize();
            this.ngOnInit();
            this.reset();
        }
    }

}

